import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, EmptyState, ConfirmDelete, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { DepotField, SimpleSelect } from "../components/DepotField";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, money, dateTimeFmt, todayStr, TZ, PAYMENT_METHODS, EXPENSE_CATEGORIES } from "../lib/api";

function ExpenseForm({ onDone }) {
  const { user, isAdmin } = useAuth();
  const { depotParam, depots } = useDepot();
  const [depotId, setDepotId] = useState("");
  const [category, setCategory] = useState(EXPENSE_CATEGORIES[0]);
  const [amount, setAmount] = useState("");
  const [payment, setPayment] = useState("Cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) setDepotId(user.depot_id);
    else if (depotParam) setDepotId(depotParam);
    else if (!depotId && depots.length) setDepotId(depots[0].id);
  }, [isAdmin, user, depotParam, depots, depotId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/expenses", { depot_id: depotId, category, amount: Number(amount), payment_method: payment, note });
      toast.success(`Expense recorded: ${money(amount)}`);
      setAmount(""); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 p-5" data-testid="expense-form">
      <DepotField value={depotId} onChange={setDepotId} testid="expense-depot-select" />
      <SimpleSelect label="Category" value={category} onChange={setCategory} options={EXPENSE_CATEGORIES} testid="expense-category-select" />
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="label-mono">Amount (TSh)</Label>
          <Input type="number" step="0.01" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-slate-950" data-testid="expense-amount-input" />
        </div>
        <SimpleSelect label="Paid via" value={payment} onChange={setPayment} options={PAYMENT_METHODS} testid="expense-payment-select" />
      </div>
      <div className="space-y-1.5">
        <Label className="label-mono">Note</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Fuel for delivery truck" className="bg-slate-950" data-testid="expense-note-input" />
      </div>
      <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="expense-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record expense
      </Button>
    </form>
  );
}

export default function Expenses() {
  const { isAdmin } = useAuth();
  const { depotParam, scopeLabel } = useDepot();
  const [date, setDate] = useState(todayStr());
  const { data, reload } = useApi("/expenses", { depot_id: depotParam, date, tz: TZ });
  const total = (data || []).reduce((s, x) => s + x.amount, 0);
  const remove = async (id) => {
    try { await api.delete(`/expenses/${id}`); toast.success("Expense deleted"); reload(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Expenses" subtitle="Operating costs — rent, wages, transport, utilities. Logged as cash out." />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <SectionCard title="New expense" className="xl:col-span-5 self-start"><ExpenseForm onDone={reload} /></SectionCard>
        <SectionCard className="xl:col-span-7" title={<span>Expenses · <span className="font-mono text-rose-400" data-testid="expenses-total">{money(total)}</span></span>}
          right={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-[160px] bg-slate-950" data-testid="expenses-date-input" />}>
          {!data?.length ? <EmptyState text="No expenses for this day" testid="expenses-empty" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="expenses-table">
                <thead className={TABLE_HEAD}>
                  <tr>
                    <th className="px-4 py-2.5 text-left">Time</th><th className="px-4 py-2.5 text-left">Category</th>
                    <th className="px-4 py-2.5 text-left">Depot</th><th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-left">By</th>{isAdmin && <th className="px-2" />}
                  </tr>
                </thead>
                <tbody>
                  {data.map((x) => (
                    <tr key={x.id} className={TABLE_ROW} data-testid={`expense-row-${x.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(x.created_at)}</td>
                      <td className="px-4 py-2.5"><div className="font-medium">{x.category}</div><div className="text-xs text-slate-500">{x.note || x.payment_method}</div></td>
                      <td className="px-4 py-2.5 text-slate-300">{x.depot_name}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-rose-400">{money(x.amount)}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-400">{x.user_name}</td>
                      {isAdmin && <td className="px-2"><ConfirmDelete title="Delete expense?" description="This expense will be removed from cashflow." onConfirm={() => remove(x.id)} testid={`delete-expense-${x.id}`} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
